import { useEffect, useState } from "react";
import api from "../../services/api";
import { CheckCircle2, Clock, Loader2, AlertTriangle, IndianRupee, Wallet, CreditCard, Sparkles, ShieldAlert } from "lucide-react";

/**
 * Sprint 81 — Refund Timeline Card.
 *
 * Renders a 3-step pipeline (Initiated → Processing → Completed) per
 * active refund on the order, plus an ETA banner ("expected by …").
 *
 * Props
 *   trackingId      : string
 *   refreshKey      : optional any — bump to force re-fetch (e.g. after edit)
 *   onAnyChange     : optional () => void  fires once data first loads
 */
export default function RefundTimelineCard({ trackingId, refreshKey, onAnyChange }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!trackingId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const res = await api.get(`/orders/${trackingId}/refund-status`);
        if (!cancelled) {
          setData(res.data);
          if (onAnyChange) onAnyChange(res.data);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Could not load refund status");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingId, refreshKey]);

  if (loading) return null;
  if (err) return null;
  if (!data || !data.refunds?.length) return null;

  return (
    <div className="space-y-4" data-testid="refund-timeline-card">
      {data.refunds.map((r) => (
        <RefundRow key={r.refund_id} refund={r} />
      ))}
    </div>
  );
}

function RefundRow({ refund }) {
  const status = refund.refund_status;
  const isFailed = status === "failed";
  const isOnHold = status === "on_hold";
  const isRejected = status === "rejected";
  const isComplete = status === "completed";

  const methodIcon = refund.refund_method === "wallet"
    ? Wallet : refund.refund_method === "razorpay_source"
    ? CreditCard : Sparkles;
  const Icon = methodIcon;

  const cardBg = isRejected ? "bg-red-50 border-red-200"
    : isOnHold ? "bg-amber-50 border-amber-300"
    : isComplete ? "bg-emerald-50 border-emerald-200"
    : "bg-sky-50 border-sky-200";
  const headColor = isRejected ? "text-red-700"
    : isOnHold ? "text-amber-800"
    : isComplete ? "text-emerald-700"
    : "text-sky-700";

  return (
    <div
      className={`rounded-2xl border-2 p-5 ${cardBg}`}
      data-testid={`refund-row-${refund.refund_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-slate-200 shrink-0">
            <Icon className="h-5 w-5 text-slate-700" />
          </div>
          <div>
            <p className={`font-['Outfit'] text-base font-bold ${headColor}`}>
              {refund.kind_label}
            </p>
            <p className="text-xs text-slate-600">
              {refund.refund_method_label}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Amount</p>
          <p className={`text-2xl font-extrabold ${headColor} flex items-center justify-end`}>
            <IndianRupee className="h-5 w-5" />
            {refund.amount}
          </p>
        </div>
      </div>

      {/* Reason */}
      {refund.reason && (
        <p className="text-xs italic text-slate-600 mb-3">"{refund.reason}"</p>
      )}

      {/* Reference ID */}
      {(refund.razorpay_refund_id || refund.wallet_txn_id) && (
        <div className="bg-white/70 border border-slate-200 rounded-lg px-3 py-2 mb-3 text-xs">
          <span className="text-slate-500">Ref:</span>
          <span className="font-mono ml-1 font-semibold text-slate-700">
            {refund.razorpay_refund_id || refund.wallet_txn_id}
          </span>
        </div>
      )}

      {/* On-hold banner */}
      {isOnHold && (
        <div className="flex items-center gap-2 bg-white rounded-lg border border-amber-300 p-3 mb-3" data-testid={`refund-on-hold-${refund.refund_id}`}>
          <ShieldAlert className="h-4 w-4 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-800 font-medium">
            <span className="font-bold">Refund Under Review.</span> Our team has been notified and will resolve this within 24 hours.
          </p>
        </div>
      )}

      {/* Failed/retrying banner */}
      {isFailed && (
        <div className="flex items-center gap-2 bg-white rounded-lg border border-sky-300 p-3 mb-3" data-testid={`refund-retrying-${refund.refund_id}`}>
          <Loader2 className="h-4 w-4 text-sky-700 animate-spin shrink-0" />
          <p className="text-xs text-sky-800 font-medium">
            Retrying automatically (attempt {refund.retry_count}/5). No action needed.
          </p>
        </div>
      )}

      {/* Rejected banner */}
      {isRejected && (
        <div className="flex items-center gap-2 bg-white rounded-lg border border-red-300 p-3 mb-3" data-testid={`refund-rejected-${refund.refund_id}`}>
          <AlertTriangle className="h-4 w-4 text-red-700 shrink-0" />
          <p className="text-xs text-red-800 font-medium">
            Refund rejected after review. Please contact support if you have questions.
          </p>
        </div>
      )}

      {/* 3-step timeline */}
      {!isRejected && (
        <div className="space-y-2.5" data-testid={`refund-steps-${refund.refund_id}`}>
          {refund.timeline.map((step, idx) => {
            const isActive = step.done && idx === Math.max(0,
              refund.timeline.findIndex((s) => !s.done) - 1);
            const isPast = step.done;
            const isFuture = !step.done;
            return (
              <div key={step.key} className="flex items-center gap-3" data-testid={`refund-step-${refund.refund_id}-${step.key}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  isPast ? "bg-emerald-500" : isActive ? "bg-sky-500" : "bg-slate-200"
                }`}>
                  {isPast ? (
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  ) : isActive ? (
                    <Loader2 className="h-3 w-3 text-white animate-spin" />
                  ) : (
                    <Clock className="h-3 w-3 text-slate-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${
                    isPast ? "text-slate-900" : isFuture ? "text-slate-400" : "text-slate-700"
                  }`}>
                    {step.label}
                  </p>
                  {step.at && (
                    <p className="text-[10px] text-slate-500 font-mono">{step.at.replace("T", " ").slice(0, 19)}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ETA banner */}
      {!isComplete && !isRejected && refund.due_at && (
        <div className="mt-3 pt-3 border-t border-slate-200/60 text-xs text-slate-600 flex items-center gap-1.5" data-testid={`refund-eta-${refund.refund_id}`}>
          <Clock className="h-3 w-3" />
          <span>
            Expected by <span className="font-mono font-semibold">{refund.due_at.replace("T", " ").slice(0, 16)}</span>
          </span>
        </div>
      )}
    </div>
  );
}
